import { motion } from 'framer-motion'
import { Link, Navigate } from 'react-router-dom'
import { AppLayout } from '../components/AppLayout'
import { useReveal } from '../lib/reveal'
import { useIdentity } from '../lib/identity'
import { BecomeSupplierPanel } from '../components/sell/BecomeSupplierPanel'

/**
 * /sell — the become-a-supplier surface. What it renders depends on WHO is
 * asking, so no path dead-ends:
 *
 *   • ALREADY a supplier (role claim) → nothing to "become"; straight to Stock
 *     feeds (/sell/integrations). We wait for identity to resolve first so a
 *     supplier never flashes the pitch.
 *   • Signed-OUT visitor → the marketing pitch, whose CTAs hand off to the ONE
 *     signup flow (/signup?type=supplier — wizard with supplier preselected,
 *     including the stock-connect step). The old /sell/apply application is dead.
 *   • Signed-IN buyer/broker → the LIGHTWEIGHT in-app connect flow
 *     (BecomeSupplierPanel). They already have an account, so they must NOT be
 *     bounced through the wizard — /signup redirects an already-signed-in user
 *     straight to /search, which is exactly the "become a supplier under Sell
 *     messes it up" dead end this replaces.
 */
export function Sell() {
  const identity = useIdentity()
  if (identity.status === 'loading') return null
  if (identity.isSupplier) return <Navigate to="/sell/integrations" replace />
  if (identity.signedIn) {
    return (
      <AppLayout>
        <BecomeSupplierPanel />
      </AppLayout>
    )
  }
  return (
    <AppLayout>
      <Hero />
      <HowItWorks />
      <Pricing />
      <CTA />
    </AppLayout>
  )
}

function Hero() {
  return (
    <section className="mx-auto max-w-5xl px-6 pt-20 pb-16 text-center">
      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xs font-semibold uppercase tracking-[0.18em] text-primary"
      >
        Sell with DiaEx
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-5 font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight text-ink"
      >
        Your inventory,
        <br />
        <span className="wordmark-glow">in front of buyers who actually buy.</span>
      </motion.h1>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.25 }}
        className="mx-auto mt-6 max-w-xl text-base text-muted leading-relaxed"
      >
        Aska's been moving diamonds for two decades — Mumbai, New York, every major US wholesaler.
        List your stock with us and skip the cold outreach.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
      >
        <Link to="/signup?type=supplier" className="btn-primary px-8">
          Become a supplier
        </Link>
        <a href="#how" className="btn-secondary px-8">
          How it works
        </a>
      </motion.div>
    </section>
  )
}

function HowItWorks() {
  return (
    <section id="how" className="mx-auto max-w-5xl px-6 py-16">
      <h2 className="text-3xl font-bold text-ink tracking-tight text-center mb-12">
        Three steps to your first sale
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Step n={1} title="Sign up" body="Create your supplier account in a couple of minutes — company details, proof of company, and how you'll send us stock. We verify within 1–2 business days." delay={1} />
        <Step n={2} title="Connect your inventory" body="Point us at your API feed (test it live during signup), upload a stock sheet, or have your ERP push to our endpoint — pick whatever fits your stack. Our AI maps your fields to ours so there's no manual data entry on either side." delay={2} />
        <Step n={3} title="Connect with buyers" body="Buyers browse your listings, see your pricing, and contact you directly by call, WhatsApp, or email to negotiate and close — you deal with them one-on-one, no middleman." delay={3} />
      </div>
    </section>
  )
}

function Step({ n, title, body, delay }: { n: number; title: string; body: string; delay: 1 | 2 | 3 }) {
  const ref = useReveal<HTMLDivElement>()
  return (
    <div ref={ref} className="card-hover reveal" data-reveal-delay={delay}>
      <div className="flex items-center gap-3 mb-3">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white text-sm font-bold">
          {n}
        </span>
        <p className="text-base font-semibold text-ink">{title}</p>
      </div>
      <p className="text-sm leading-relaxed text-muted">{body}</p>
    </div>
  )
}

/**
 * Pricing — listing is free, full stop. The "DiaEx store" subscription
 * pitch was REMOVED (Tej 2026-08-03): storefront checkout doesn't exist
 * yet, so the page must not sell it. Re-add the upgrade card when the
 * storefront ships (subscription tier decision is task #43).
 */
function Pricing() {
  return (
    <section className="mx-auto max-w-5xl px-6 py-16">
      <div className="card text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-soft mb-2">Free to list</p>
        <h3 className="text-2xl font-bold text-ink mb-3">Listing is free.</h3>
        <p className="text-sm text-muted max-w-xl mx-auto leading-relaxed">
          List your inventory and take buyer enquiries at no cost. When a buyer purchases
          through Aska Services, you receive your listed price in full — the buyer pays the
          service fee, not you.
        </p>
      </div>
    </section>
  )
}

function CTA() {
  return (
    <section className="mx-auto max-w-3xl px-6 py-20 text-center">
      <h2 className="text-3xl font-bold text-ink tracking-tight">Ready to list?</h2>
      <p className="mt-4 text-sm text-muted">
        Sign up in a couple of minutes — tell us about your business and how you'll send your stock, and
        we'll verify you within 1–2 business days.
      </p>
      <Link
        to="/signup?type=supplier"
        className="btn-primary mt-8 inline-flex"
      >
        Become a supplier
      </Link>
    </section>
  )
}
